import { noop } from 'es-toolkit';
import HLSEngine from '../HLSEngine';
export default class HLSLiveSeekEngine extends HLSEngine {
    static name = 'hlsliveseek';
    #currentTime = -1;
    #duration = 0;
    #staticCurrentTime = -1;
    #staticDuration = 0;
    #previousSeekPromise;
    #wasPaused = false;
    constructor(options) {
        const defaultStartPosition = -1;
        super(options, defaultStartPosition);
    }
    #registerEvents() {
        this.on('timeupdate', this.#onTimeUpdate);
        this.on('meta', this.#onMeta);
        this.on('pause', this.#onPause);
    }
    #deregisterEvents() {
        this.off('timeupdate', this.#onTimeUpdate);
        this.off('meta', this.#onMeta);
        this.off('pause', this.#onPause);
    }
    #onPause = () => {
        this.#wasPaused = true;
    };
    #onTimeUpdate = () => {
        const currentTime = super.getCurrentTime();
        if (!currentTime && this.#currentTime !== -1) {
            return;
        }
        this.#currentTime = this.#staticCurrentTime + currentTime;
        this.#duration = this.#staticDuration + currentTime;
    };
    #onMeta = (data) => {
        data.tags.forEach((tag) => {
            if (tag.key === this._options.hlsLiveSeekFragTagKey) {
                this.#duration = Number.parseInt(tag.value);
                this.#currentTime =
                    this.#currentTime === -1 || this.#staticCurrentTime === -1
                        ? this.#duration
                        : this.#currentTime;
                this.#staticDuration = this.#duration;
                this.#staticCurrentTime = this.#currentTime;
            }
        });
    };
    #buildUrl(seekTime) {
        const url = new URL(this._options.src);
        const posStart = Math.round(seekTime) || 1;
        url.searchParams.set('pos_start', posStart.toString());
        return url.toString();
    }
    isLive() {
        if (this.#currentTime === -1) {
            return true;
        }
        return super.isLive() && this.#currentTime === this.#duration && !this.#wasPaused;
    }
    seekBackward(seekOffset) {
        this.seekTo(this.#currentTime - seekOffset);
    }
    seekForward(seekOffset) {
        this.seekTo(this.#currentTime + seekOffset);
    }
    async seekTo(seekTime) {
        await this.#previousSeekPromise;
        const { promise, resolve } = Promise.withResolvers();
        const isLiveHead = seekTime === -1 || this.#duration - seekTime <= 1;
        const wasPlaying = this.isPlaying();
        const currentVolume = this.getVolume();
        this.#previousSeekPromise = promise;
        this.#staticCurrentTime = isLiveHead ? -1 : seekTime;
        this.#staticDuration = 0;
        this.#currentTime = isLiveHead ? this.#duration : Math.round(seekTime);
        this.emit('seeking');
        this.destroy();
        this.setup();
        this.setOptions({ src: this.#buildUrl(this.#currentTime) });
        this.emit('timeupdate');
        try {
            await this.load();
        }
        catch {
            this.#deregisterEvents();
            resolve();
            return;
        }
        this.emit('seeked');
        if (wasPlaying || this.isPlaying() || isLiveHead) {
            this.setVolume(currentVolume);
            await this.play().catch(noop);
        }
        else {
            this.setVolume(0);
            await this.play().catch(noop);
            this.pause();
            this.setVolume(currentVolume);
        }
        resolve();
    }
    getDuration = () => {
        return this.#duration || 0;
    };
    getCurrentTime = () => {
        if (this.#currentTime === -1) {
            return 0;
        }
        return this.#currentTime || 0;
    };
    async load() {
        this.#wasPaused = false;
        this.#registerEvents();
        await super.load();
    }
    destroy() {
        this.#deregisterEvents();
        super.destroy();
    }
}
